export const EMR_API = {
  app: {
    base: '/app',
    layout: '/layout'
  },
  // Staff user *management* is not served here. It moved to admin-web Next.js
  // server actions talking to Mongo `crm-users` directly. This controller stays
  // read-mostly for emr-web until that frontend is sunsetted; crm-api keeps its
  // own `/users` for crm-web. Do not add management endpoints here.
  users: {
    base: '/users',
    resetPass: '/password/reset',
    getCurrent: '/current'
  },
  notifications: {
    base: '/notifications',
    dismissAll: '/dismiss-all',
    dismissSingle: '/:id/dismiss'
  },
  tasks: {
    base: '/tasks',
    single: '/:id'
  },
  customers: {
    base: '/customers',
    single: '/:id',
    lead: '/:id/lead',
    lookups: '/lookups',
    files: '/:id/files',
    watchlist: '/:id/watchlist',
    paymentMethods: '/:id/payment-methods',
    credits: '/:id/credits',
    labResult: '/:id/lab-results',
    extras: '/:id/extras',
    pharmacySearch: '/pharmacies/search'
  },
  consultations: {
    base: '/consultations',
    latest: '/latest',
    required: '/required',
    satisfied: '/satisfied',
    viewerToken: '/:id/viewer-token'
  },
  appointments: {
    base: '/appointments',
    single: '/:id',
    mySeen: '/my-seen',
    pull: '/start',
    meeting: '/meeting'
  },
  products: {
    base: '/products',
    categories: '/categories',
    single_category: '/:category',
    variants: '/:productId/variants'
  },
  // The canonical catalog surface. Unlike v1 it disambiguates `/:id` from
  // `/categories/:category`, so the crm-api write endpoints could be merged in
  // here without a positional collision — see docs/admin-web-consolidation.md (A4).
  productsv2: {
    base: '/v2/products',
    categories: '/categories',
    single: '/:id',
    single_category: '/categories/:category',
    variants: '/:productId/variants',
    lookups: '/lookups',
    uploadFile: '/:id/files',
    fileSingle: '/:id/files/:fileName'
  },
  productCategories: {
    base: '/product-categories',
    single: '/:id',
    lookups: '/lookups'
  },
  orders: {
    base: '/orders',
    archived: '/archived',
    single: '/:orderId',
    singleDiscount: '/:orderId/discount',
    singleRequirements: '/:orderId/requirements',
    prescriptions: '/:orderId/prescriptions',
    failedWorkflowRuns: '/:orderId/failed-workflow-runs',
    retrigger: "/retrigger",
    payments: '/:orderId/payments',
    refund: '/:orderId/refund',
    refillItem: '/:orderId/products/:productId/refill',
    exportCsv: '/export/csv',
    visit: '/:orderId/visit',
    visitDrugs: '/:orderId/visit/drugs'
  },
  invoices: {
    base: '/invoices',
    sendToPatient: '/send-patient'
  },
  drugs: {
    base: '/drugs'
  },
  notes: {
    base: '/notes',
    single: '/:id',
    sessionReport: 'session-report/:id'
  },
  messages: {
    base: '/messages',
    query: '/query',
    outbound: '/outbound',
    faxRx: '/fax/rx',
    upload: '/upload',
    forward: '/forward',
    list: ''
  },
  cannedMessages: {
    base: '/canned-messages',
    single: '/:id',
    categories: '/categories',
    categorySingle: '/categories/:id'
  },
  prescriptions: {
    base: '/prescriptions',
    single: '/:id',
    fulfillments: '/:id/fulfillments',
    fax: '/:id/fax',
    pdf: '/:id/pdf',
    sendPdfToChat: '/:id/send-pdf-to-chat'
  },
  files: {
    base: '/files',
    link: '/:id/file-by-link',
    uploadBranding: '/branding'
  },
  subscriptions: {
    base: '/subscriptions',
    single: '/:id',
    exportCsv: '/export/csv'
  },
  labReq: {
    base: '/lab-req'
  },
  labResult: {
    base: '/lab-results',
    single: '/:id',
    uploadFile: '/:resourceId/upload/:faxId',
    getFile: '/file/:resourceId/:fileName',
    pdf: '/:id/pdf',
    merge: '/:id/merge',
    createFromForm: '/create',
    submitPdf: '/submit-pdf'
  },
  chat: {
    base: '/chat',
    join: '/join',
    getToken: '/token',
    refreshToken: '/token/refresh'
  },
  emailTemplates: {
    base: '/email-templates',
    single: '/:id',
    invoice: '/invoice',
    test: '/test',
    lookups: '/lookups',
    // Merged from CRM_API.emailTemplates — see docs/admin-web-consolidation.md.
    clone: '/clone'
  },
  activities: {
    base: '/activities',
    single: '/:id'
  },
  pharmacies: {
    base: '/pharmacies',
    single: '/:id'
  },
  // API key *management* is not served here. Keys are issued from admin-web
  // server actions against Mongo `api-keys`. LocalJwtGuard still *accepts*
  // keys as credentials on every other route.
  preAuth: {
    base: '/pre-auth',
    create: '/create'
  },

  /* ------------------------------------------------------------------ */
  /* Merged from crm-api — see docs/admin-web-consolidation.md (A2/A3).  */
  /* Paths are kept identical to CRM_API so admin-web only has to change */
  /* which base URL it calls, not the path.                              */
  /* ------------------------------------------------------------------ */
  coupons: {
    base: '/coupons',
    single: '/:id'
  },
  promotions: {
    base: '/promotions',
    single: '/:id'
  },
  orderStats: {
    base: '/order-stats'
  },
  segments: {
    base: '/segments',
    lookups: '/lookups',
    single: '/:id'
  },
  campaigns: {
    base: '/campaigns',
    single: '/:id',
    launch: '/:id/launch'
  },
  workflows: {
    base: '/workflows',
    lookups: '/lookups',
    single: '/:id',
    singleByName: '/name/:name',
    singleTest: '/:id/test',
    adapters: '/adapters'
  },
  workflowRuns: {
    base: '/workflows-runs',
    single: '/:id',
    updateFromBlueprint: '/:id/update-from-blueprint',
    rerun: '/:id/rerun'
  },
  analytics: {
    base: '/analytics/v2',
    dashboard: '/dashboard'
  },
  // Admin helpdesk (tickets ported from jackjill chat-api + medical inbox).
  // Contract consumer: apps/admin-web/lib/helpdesk/api.ts.
  helpdesk: {
    base: '/helpdesk',
    tickets: '/tickets',
    ticketSingle: '/tickets/:id',
    ticketMessages: '/tickets/:id/messages',
    ticketNotes: '/tickets/:id/notes',
    counts: '/counts',
    agents: '/agents',
    macros: '/macros',
    conversations: '/conversations',
    conversationSingle: '/conversations/:customerId',
    conversationMessages: '/conversations/:customerId/messages',
    conversationRead: '/conversations/:customerId/read',
    conversationFlag: '/conversations/:customerId/flag'
  },
  // Gorgias inbound webhook (HMAC-verified, no JWT).
  gorgias: {
    base: '/gorgias',
    webhook: '/webhook'
  }
};
